import React, { useState } from "react";
import { ReactComponent as Bell } from "../../assets/icons/bell-icon.svg";
import { FiCopy, FiCheck, FiLoader } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { setModalOpen } from "../../store/modalSlice";
import ModalBase from "./ModalBase";

interface NotificationProps {
  header: string;
  details: React.ReactNode;
  /** Optional shareable URL — rendered in a read-only box with a copy button. */
  url?: string;
  /** When true, show a spinner and hide the Ok button (e.g. while generating a link). */
  loading?: boolean;
  onClose?: (result?: any) => void;
}

const Notification: React.FC<NotificationProps> = ({ header, details, url, loading, onClose }) => {
  const dispatch = useDispatch();
  const [copied, setCopied] = useState(false);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      dispatch(setModalOpen(false));
    }
  };

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  return (
    <ModalBase
      title={header}
      icon={<Bell className="h-6 w-6" />}
      onClose={handleClose}
      size="sm"
      /* No footer while loading — the link isn't ready, so there is nothing to dismiss to */
      footer={
        loading ? undefined : (
          <button type="button" className="eai-btn-outline" onClick={handleClose}>
            Ok
          </button>
        )
      }
    >
      <div className="flex flex-col gap-3">
        {loading && (
          <div className="eai-description-sm flex items-center gap-2">
            <FiLoader className="h-4 w-4 animate-spin text-eai-cta-primary" />
            <span>Working…</span>
          </div>
        )}
        {url && (
          <div className="flex items-center gap-2 rounded-eai-card border border-eai-border bg-eai-surface-sunken p-2">
            <input
              type="text"
              readOnly
              value={url}
              onFocus={(e) => e.target.select()}
              className="min-w-0 flex-1 truncate bg-transparent font-inter text-eai-body-sm text-eai-ink outline-none"
              aria-label="Share link"
            />
            <button type="button" onClick={handleCopy} className="eai-btn-cta-toolbar shrink-0">
              {copied ? <FiCheck className="h-3.5 w-3.5" /> : <FiCopy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        )}
        {details && <div className="eai-description-sm">{details}</div>}
      </div>
    </ModalBase>
  );
};

export default Notification;
